import React, { useState } from 'react';
import {
  PageLayout,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Switch,
  ThemeSwitcher,
} from '@lognai/synapse-design-system-v2';
import { useAuth } from '../hooks/useAuth';
import {
  usePreferences,
  useUpdatePreferences,
  useChangePassword,
} from '../hooks/useAccountSettings';
import { PrimaryColorPicker } from '../components/PrimaryColorPicker';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{value || '—'}</span>
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const { user, company } = useAuth();

  // Security
  const changePassword = useChangePassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const canSubmit = currentPassword.length > 0 && newPassword.length >= 8 && newPassword === confirm;

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setCurrentPassword('');
          setNewPassword('');
          setConfirm('');
        },
      }
    );
  };

  // Notifications
  const prefsQuery = usePreferences();
  const updatePrefs = useUpdatePreferences();
  const emailEnabled = prefsQuery.data?.notifications?.email !== false;

  return (
    <PageLayout className="animate-fade-in-up">
      <h1 className="text-2xl font-semibold mb-4">Settings</h1>

      <div className="flex flex-col gap-4 max-w-2xl">
        {/* Profile */}
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
            <CardDescription>Your account details.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full name" value={user?.fullName} />
            <Field label="Email" value={user?.email} />
            <Field label="Roles" value={user?.roles?.join(', ')} />
            {company?.name && <Field label="Company" value={company.name} />}
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle>Security</CardTitle>
            <CardDescription>Change your password.</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="flex flex-col gap-3" onSubmit={handlePasswordSubmit}>
              <Input
                type="password"
                placeholder="Current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
              />
              <Input
                type="password"
                placeholder="New password (min 8 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
              <Input
                type="password"
                placeholder="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
              {confirm.length > 0 && newPassword !== confirm && (
                <p className="text-sm text-destructive">Passwords do not match.</p>
              )}
              {changePassword.isSuccess && (
                <p className="text-sm text-success-foreground">Password updated</p>
              )}
              {changePassword.isError && (
                <p className="text-sm text-destructive">
                  {(changePassword.error as Error)?.message || 'Failed to change password'}
                </p>
              )}
              <div>
                <Button type="submit" disabled={!canSubmit || changePassword.isPending}>
                  {changePassword.isPending ? 'Updating…' : 'Update password'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card>
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
            <CardDescription>Choose your theme and brand color.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <p className="text-sm font-medium text-foreground mb-2">Theme</p>
              <ThemeSwitcher />
            </div>
            <PrimaryColorPicker />
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle>Notifications</CardTitle>
            <CardDescription>Manage how we contact you.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-foreground">Email notifications</span>
                <span className="text-xs text-muted-foreground">
                  Receive notification emails (the only channel currently delivered).
                </span>
                {prefsQuery.isError && (
                  <span className="text-xs text-destructive">
                    Couldn’t load preferences; showing defaults.
                  </span>
                )}
                {updatePrefs.isError && (
                  <span className="text-xs text-destructive">
                    Failed to save preference. Please try again.
                  </span>
                )}
              </div>
              <Switch
                checked={emailEnabled}
                disabled={prefsQuery.isLoading || updatePrefs.isPending}
                onCheckedChange={(checked) =>
                  updatePrefs.mutate({ notifications: { email: checked } })
                }
                aria-label="Email notifications"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default SettingsPage;
